import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <article className="group relative overflow-hidden rounded-lg">
        <Link to={`/article/${article.slug}`} className="block">
          <div className="relative w-full h-[420px] md:h-[500px] overflow-hidden rounded-lg">
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-black/60 z-10"></div>
            <img
              src={article.coverImage}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-8">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full mb-3">
                {article.category}
              </span>
              <h2 className="text-xl md:text-3xl font-heading font-bold text-white mb-2 leading-tight line-clamp-2">
                {article.title}
              </h2>
              <p className="text-sm text-white/80 line-clamp-2 mb-3 max-w-2xl">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-3 text-xs text-white/70">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <span>{article.author}</span>
                </div>
                <span>{article.date}</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group">
        <Link to={`/article/${article.slug}`} className="flex items-start gap-4">
          <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-1">
              {article.title}
            </h4>
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <span>{article.date}</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200/50 transition-all duration-300">
      <Link to={`/article/${article.slug}`}>
        <div className="relative w-full h-48 md:h-56 overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3">
            <span className="inline-block px-2.5 py-1 text-xs font-medium bg-background-50/90 text-foreground-800 rounded-full">
              {article.category}
            </span>
          </div>
        </div>
        <div className="p-4 md:p-5">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
              </div>
              <span className="text-xs text-foreground-600">{article.author}</span>
            </div>
            <span className="text-xs text-foreground-500">{article.date}</span>
          </div>
        </div>
      </Link>
    </article>
  );
}